import React, { useEffect, useState } from 'react'
import {
  Container,
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  alpha,
  useTheme,
  Stack,
  LinearProgress,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  CheckCircle,
  ShieldCheck,
  ArrowLeft,
} from 'lucide-react'
import axios from 'axios'
import { cookies } from '../../utils/cookie'
import Swal from 'sweetalert2'
import { jwtDecode } from 'jwt-decode'
import { useFormik } from 'formik'
import { resetPasswordFormSchema } from '../../utils/formSchema/formSchemas'

const ModernResetPassword = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const token = queryParams.get('token')
  const navigate = useNavigate()
  const theme = useTheme()

  const [showPassword, setShowPassword] = useState(false)
  const [showVerifyPassword, setShowVerifyPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)

  useEffect(() => {
    let decodeToken
    if (token) {
      try {
        decodeToken = jwtDecode(token)
        setTokenValid(true)
      } catch (error) {
        decodeToken = null
        setTokenValid(false)
      }

      if (!decodeToken) {
        Swal.fire({
          icon: 'error',
          title: 'Geçersiz Bağlantı',
          text: 'Lütfen e-postanızdaki bağlantıyı kontrol edin.',
          confirmButtonText: 'Tamam',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          confirmButtonColor: theme.palette.primary.main,
        })
        navigate('/forgot-password')
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Bağlantı Bulunamadı',
        text: 'Lütfen e-postanızdaki şifre sıfırlama bağlantısını kullanın.',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
      navigate('/forgot-password')
    }
  }, [])

  const submit = async (values) => {
    setIsLoading(true)
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/reset-password?token=${token}`,
        {
          password: values.password,
        },
        {
          headers: {
            Authorization: `Bearer ${cookies.get('jwt-access')}`,
            'Content-Type': 'application/json',
          },
        }
      )

      Swal.fire({
        icon: 'success',
        title: 'Şifre Değiştirildi!',
        html: `
          <p>Şifreniz başarıyla değiştirildi.</p>
          <p style="margin-top: 12px; font-size: 0.9em; color: #666;">
            Artık yeni şifrenizle giriş yapabilirsiniz.
          </p>
        `,
        confirmButtonText: 'Giriş Yap',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      }).then(() => {
        navigate('/login')
      })
    } catch (err) {
      setIsLoading(false)
      const message = err.response?.data?.message || 'Şifre değiştirilemedi'
      Swal.fire({
        icon: 'error',
        title: 'Bir Hata Oluştu',
        text: message,
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    }
  }

  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues: {
      password: '',
      verifyPassword: '',
    },
    validationSchema: resetPasswordFormSchema,
    onSubmit: submit,
  })

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' }
    
    let strength = 0
    if (password.length >= 8) strength += 25
    if (password.match(/[a-z]/)) strength += 25
    if (password.match(/[A-Z]/)) strength += 25
    if (password.match(/[0-9]/)) strength += 25

    if (strength <= 25) return { strength, label: 'Zayıf', color: theme.palette.error.main }
    if (strength <= 50) return { strength, label: 'Orta', color: theme.palette.warning.main }
    if (strength <= 75) return { strength, label: 'İyi', color: theme.palette.info.main }
    return { strength, label: 'Güçlü', color: theme.palette.success.main }
  }

  const passwordStrength = getPasswordStrength(values.password)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.background.default,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Gradient Orbs */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 2,
            }}
            sx={{
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.15
              )}, transparent)`,
              filter: 'blur(60px)',
              top: `${20 + i * 30}%`,
              left: `${10 + i * 30}%`,
            }}
          />
        ))}

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            opacity: 0.3,
          }}
        />

        {/* Floating Key Icons */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 15, 0],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 1.5,
            }}
            sx={{
              position: 'absolute',
              top: `${10 + i * 20}%`,
              left: `${5 + i * 20}%`,
              opacity: 0.1,
            }}
          >
            <KeyRound size={40} color={theme.palette.primary.main} />
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Icon */}
          <Box
            component={motion.div}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, type: 'spring' }}
            sx={{
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.2
              )}, ${alpha(theme.palette.primary.main, 0.1)})`,
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              mb: 3,
            }}
          >
            <ShieldCheck size={48} color={theme.palette.primary.main} />
          </Box>

          {/* Header */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Yeni Şifre Oluştur
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: alpha('#fff', 0.7),
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            Lütfen yeni şifrenizi girin. Güçlü bir şifre seçtiğinizden emin olun.
          </Typography>

          {/* Form */}
          <Stack spacing={3}>
            {/* Password Field */}
            <Box>
              <TextField
                id="password"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Yeni Şifre"
                placeholder="••••••••"
                value={values.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={20} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: alpha('#fff', 0.6),
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />

              {/* Password Strength Indicator */}
              {values.password && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                      Şifre Gücü
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: passwordStrength.color, fontWeight: 600 }}
                    >
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.strength}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha('#fff', 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: passwordStrength.color,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {[
                      { check: values.password.length >= 8, label: '8+ karakter' },
                      { check: values.password.match(/[a-z]/), label: 'Küçük harf' },
                      { check: values.password.match(/[A-Z]/), label: 'Büyük harf' },
                      { check: values.password.match(/[0-9]/), label: 'Rakam' },
                    ].map((item, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: item.check
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha('#fff', 0.05),
                          border: `1px solid ${
                            item.check
                              ? alpha(theme.palette.success.main, 0.3)
                              : alpha('#fff', 0.1)
                          }`,
                        }}
                      >
                        <CheckCircle
                          size={12}
                          color={
                            item.check
                              ? theme.palette.success.main
                              : alpha('#fff', 0.3)
                          }
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: item.check
                              ? theme.palette.success.main
                              : alpha('#fff', 0.5),
                            fontSize: '0.7rem',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Verify Password Field */}
            <Box>
              <TextField
                id="verifyPassword"
                fullWidth
                type={showVerifyPassword ? 'text' : 'password'}
                label="Şifre Tekrarı"
                placeholder="••••••••"
                value={values.verifyPassword}
                onChange={handleChange}
                error={!!errors.verifyPassword}
                helperText={errors.verifyPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={20} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                        edge="end"
                        sx={{
                          color: alpha('#fff', 0.6),
                          '&:hover': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {showVerifyPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#000',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
                '&:disabled': {
                  background: alpha(theme.palette.primary.main, 0.5),
                  color: alpha('#000', 0.5),
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Şifre Değiştiriliyor...' : 'Şifreyi Değiştir'}
            </Button>

            {isLoading && <LinearProgress sx={{ borderRadius: 1 }} />}

            {/* Back to Login */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/login')}
              startIcon={<ArrowLeft size={18} />}
              sx={{
                py: 1.5,
                borderRadius: 2,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              Giriş Sayfasına Dön
            </Button>
          </Stack>
        </Paper>

        {/* Security Tips */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            background: alpha(theme.palette.info.main, 0.1),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.info.main,
              display: 'block',
              fontWeight: 600,
              mb: 1,
            }}
          >
            💡 Güvenlik İpuçları
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
              • En az 8 karakter kullanın
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
              • Büyük ve küçük harfleri karıştırın
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
              • Rakam ve özel karakterler ekleyin
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
              • Kolay tahmin edilebilecek kelimeler kullanmayın
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default ModernResetPassword